import { toDurableEventLogLines } from './durable-event-log-lines';
import { eventTypeToSeverity, mapEventTypeToTitle } from './event-utils';
import { RunActivityTimeline } from './run-activity-timeline';
import { TabOption } from './step-run-detail/step-run-detail';
import {
  LogLine,
  V1LogLineLevelIncludingEvictionNotice,
} from '@/components/v1/cloud/logging/log-search/use-logs';
import { getTaskStartTimes } from '@/components/v1/cloud/logging/step-deltas';
import { useRefetchInterval } from '@/contexts/refetch-interval-context';
import { useSidePanel } from '@/hooks/use-side-panel';
import { useCurrentTenantId } from '@/hooks/use-tenant';
import { V1LogLineLevel, V1TaskEvent, queries } from '@/lib/api';
import api from '@/lib/api/api';
import { collectPages } from '@/lib/collect-pages';
import { emptyGolangUUID } from '@/lib/utils';
import { appRoutes } from '@/router';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useRef } from 'react';

export type EventWithMetadata = V1TaskEvent & {
  metadata: {
    id: string;
  };
};

const EVENTS_PER_PAGE = 100;

export function StepRunEvents({
  taskRunId,
  workflowRunId,
  isDurable,
  durableTaskIds,
  taskExternalIds,
  events: externalEvents,
}: {
  taskRunId?: string;
  workflowRunId?: string;
  isDurable?: boolean;
  durableTaskIds?: string[];
  taskExternalIds?: string[];
  events?: V1TaskEvent[];
  taskDisplayName?: string;
  fallbackTaskDisplayName?: string;
  onClick?: (stepRunId: string) => void;
}) {
  const isDag =
    (!taskRunId && !!workflowRunId) ||
    (!!taskRunId && !!workflowRunId && taskRunId !== workflowRunId);

  const { tenantId } = useCurrentTenantId();
  const { refetchInterval } = useRefetchInterval();
  const { open } = useSidePanel();
  const executingRef = useRef(false);

  const eventsQuery = useQuery({
    queryKey: ['run-events', tenantId, taskRunId, workflowRunId],
    queryFn: async () => {
      if (taskRunId) {
        return collectPages(async (offset, limit) => {
          const response = await api.v1TaskEventList(taskRunId, {
            offset,
            limit,
          });
          return response.data.rows ?? [];
        }, EVENTS_PER_PAGE);
      }

      if (workflowRunId) {
        const response = await api.v1WorkflowRunTaskEventsList(workflowRunId);
        return response.data.rows ?? [];
      }

      throw new Error('A task or workflow run is required');
    },
    enabled: !externalEvents,
    refetchInterval: externalEvents ? false : refetchInterval,
  });

  // fixme: this is an n+1 query, would be better to have a bulk getter
  const durableLogsQueries = useQueries({
    queries: [
      ...new Set([
        ...(durableTaskIds ?? []),
        ...(taskRunId && isDurable ? [taskRunId] : []),
      ]),
    ].map((id) => ({
      ...queries.v1DurableTasks.eventLog(tenantId, id),
      refetchInterval: 5000,
    })),
  });

  const logs = useMemo(() => {
    const taskLines = toTaskEventLogLines(
      externalEvents ?? eventsQuery.data ?? [],
      tenantId,
    );
    const durableEventLogLines = durableLogsQueries.flatMap((q) =>
      toDurableEventLogLines(q.data ?? []),
    );
    return [...taskLines, ...durableEventLogLines];
  }, [externalEvents, eventsQuery.data, tenantId, durableLogsQueries]);

  const handleTaskRunExpand = useCallback(
    (taskRunId: string) => {
      // hack to prevent click handler from firing multiple times,
      // causing index offset issues
      if (executingRef.current) {
        return;
      }

      executingRef.current = true;

      open({
        type: 'task-run-details',
        content: {
          taskRunId,
          defaultOpenTab: TabOption.Activity,
          showViewTaskRunButton: true,
        },
      });

      setTimeout(() => {
        executingRef.current = false;
      }, 100);
    },
    [open],
  );

  const logTaskIds = taskRunId ? [taskRunId] : (taskExternalIds ?? []);
  const taskStartTimes = useMemo(
    () => getTaskStartTimes(externalEvents ?? eventsQuery.data ?? []),
    [externalEvents, eventsQuery.data],
  );
  const hasEventsError = !externalEvents && eventsQuery.isError;
  const hasDurableError = durableLogsQueries.some((query) => query.isError);
  const eventError = hasEventsError
    ? 'Run events could not be loaded. Activity may be incomplete.'
    : hasDurableError
      ? 'Durable events could not be loaded. Activity may be incomplete.'
      : undefined;

  return (
    <RunActivityTimeline
      key={`${tenantId}:${taskRunId ?? workflowRunId}`}
      events={logs}
      taskExternalIds={logTaskIds}
      taskStartTimes={taskStartTimes}
      showTaskName={isDag}
      isLoadingEvents={
        (!externalEvents && eventsQuery.isLoading) ||
        durableLogsQueries.some((query) => query.isLoading)
      }
      eventError={eventError}
      onViewRun={handleTaskRunExpand}
    />
  );
}

function toTaskEventLogLines(
  events: V1TaskEvent[],
  tenantId: string,
): LogLine[] {
  return events.map((event) => {
    const severity = eventTypeToSeverity(event.eventType);
    let level: V1LogLineLevelIncludingEvictionNotice;

    if (event.eventType === 'DURABLE_EVICTED') {
      level = 'EVICTION_NOTICE';
    } else if (event.eventType === 'DURABLE_RESTORING') {
      level = 'RESTORE_NOTICE';
    } else {
      switch (severity) {
        case 'CRITICAL':
          level = V1LogLineLevel.ERROR;
          break;
        case 'WARNING':
          level = V1LogLineLevel.WARN;
          break;
        case 'INFO':
          level = V1LogLineLevel.INFO;
          break;
        default:
          level = V1LogLineLevel.DEBUG;
          break;
      }
    }

    let line = mapEventTypeToTitle(event.eventType);
    if (event.message) {
      line += `: ${event.message}`;
    }

    const linkTo =
      event.workerId && event.workerId !== emptyGolangUUID
        ? {
            destination: appRoutes.tenantWorkerRoute.to,
            params: { tenant: tenantId, worker: event.workerId },
            hoverText: 'View worker',
          }
        : undefined;

    return {
      id: `${event.taskId}:task-event:${String(event.id).padStart(20, '0')}`,
      source: 'event',
      attempt: event.attempt,
      timestamp: event.timestamp,
      level,
      line,
      taskDisplayName: event.taskDisplayName,
      taskExternalId: event.taskId,
      error: event.errorMessage,
      linkTo,
    };
  });
}
